import { rows } from '@/db/database'
import { currentDevice } from '@/lib/device'
import { newTempId } from '@/lib/tempId'
import { nextNumber } from '@/db/repositories/orders'
import { syncEngine } from '@/sync/SyncEngine'
import { Paginated, PosHttpParams, PosModel, PosSync } from '@/types'

type Tier = { name: string; min_points: number; discount_percentage: number }

/** The active tier with the highest threshold the points reach (§7.3); none below the lowest. */
export const tierFor = (points: number, tiers: PosSync.LocalRow[]): PosModel.Customer['tier'] => {
  const best = (tiers as unknown as (Tier & PosSync.LocalRow)[])
    .filter((t) => (t.status ?? 'active') === 'active' && Number(t.min_points) <= points && Number(t.discount_percentage) > 0)
    .sort((a, b) => Number(b.min_points) - Number(a.min_points))[0]
  return best ? { name: String(best.name), discount_percentage: Number(best.discount_percentage) } : null
}

const loadTiers = () => rows('membership_tiers').toArray()

export const toCustomer = (row: PosSync.LocalRow, tiers: PosSync.LocalRow[] = [], books: PosSync.LocalRow[] = []): PosModel.Customer => ({
  _id: row.id,
  name: String(row.name),
  phone: String(row.phone),
  email: (row.email as string) || undefined,
  barcode: (row.barcode as string | null | undefined) ?? null,
  loyalty_points: Number(row.reward_points ?? 0),
  total_orders: 0,
  credit_limit: Number(row.credit_limit ?? 0),
  credit_used: Number(row.credit_used ?? 0),
  offline_credit_allowance: (row.offline_credit_allowance as number | null | undefined) ?? null,
  price_book_id: row.price_book_id ? String(row.price_book_id) : null,
  price_book: row.price_book_id ? (books.find((b) => String(b._id) === String(row.price_book_id))?.title as string | undefined) : undefined,
  tier: tierFor(Number(row.reward_points ?? 0), tiers),
})

export const getCustomers = async (query: PosHttpParams.CustomerList = {}): Promise<Paginated<PosModel.Customer>> => {
  const search = query.search?.trim().toLowerCase()
  const [all, tiers, books] = await Promise.all([rows('customers').toArray(), loadTiers(), rows('price_books').toArray()])
  const items = all
    .filter((c) => !c.deleted_at && c.synced !== -1)
    .filter((c) => !search || `${c.name} ${c.phone} ${c.barcode ?? ''}`.toLowerCase().includes(search))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)))
    .slice(0, 50)
    .map((c) => toCustomer(c, tiers, books))
  return { items, total: items.length }
}

/** Customers the server found online, kept like pulled rows; a local unsynced edit wins. */
export const saveFoundCustomers = async (found: PosSync.LocalRow[]) => {
  const table = rows('customers')
  for (const item of found) {
    const local = item._id ? await table.where('_id').equals(String(item._id)).first() : undefined
    if (local && local.synced !== 1) continue
    await table.put({ ...item, id: local?.id ?? String(item._id), synced: 1 })
  }
}

/** By customer card (POS.md §18.8). */
export const getCustomerByBarcode = async (code: string): Promise<PosModel.Customer | null> => {
  const row = (await rows('customers').toArray()).find((c) => !c.deleted_at && c.synced !== -1 && c.barcode === code)
  return row ? toCustomer(row, await loadTiers()) : null
}

/** Written here first and synced in the background (§15.1 rule 1); a known phone merges on the server (rule 14). */
export const createCustomer = async (body: PosHttpParams.CreateCustomer): Promise<PosModel.Customer> => {
  const device = currentDevice()
  if (!device) throw new Error('This device is not set up.')
  const barcode = body.barcode?.trim() || undefined
  if (barcode && (await getCustomerByBarcode(barcode))) throw new Error(`Card ${barcode} belongs to another customer.`)
  const tempId = newTempId(device.terminal_unique_no)
  const row: PosSync.LocalRow = {
    id: tempId,
    temp_id: tempId,
    synced: 0,
    name: body.name,
    phone: body.phone,
    email: body.email,
    barcode,
    created_at: new Date().toISOString(),
  }
  await rows('customers').add(row)
  syncEngine.requestSync()
  return toCustomer(row)
}

/**
 * Edits a customer on the device; sync sends it with its `_id` (last write wins, §15.9 rule 14). Credit, points and
 * price book stay the server's. Phone and card are unique per business, so a clash is refused here rather than
 * failing the push.
 */
export const updateCustomer = async (id: string, body: PosHttpParams.CreateCustomer): Promise<PosModel.Customer> => {
  const row = await rows('customers').get(id)
  if (!row) throw new Error('Customer not found.')
  const barcode = body.barcode?.trim() || null
  const others = (await rows('customers').toArray()).filter((c) => c.id !== id && !c.deleted_at)
  if (others.some((c) => String(c.phone) === body.phone)) throw new Error(`Another customer has the phone ${body.phone}.`)
  if (barcode && others.some((c) => c.barcode === barcode)) throw new Error(`Card ${barcode} belongs to another customer.`)
  const patch = { name: body.name, phone: body.phone, email: body.email || null, barcode, synced: 0 as const, issue_fields: undefined }
  await rows('customers').update(id, patch)
  syncEngine.requestSync()
  return toCustomer({ ...row, ...patch }, await loadTiers())
}

/**
 * A customer paying back credit, written on the device and synced (`credit_repayments`, POS.md rule 7). The server
 * lowers `credit_used` once, when it first sees it.
 */
export const recordRepayment = async (customerId: string, amount: number, method: string, remark?: string) => {
  const device = currentDevice()
  if (!device) throw new Error('This device is not set up.')
  const customer = await rows('customers').get(customerId)
  if (!customer) throw new Error('Customer not found.')
  if (amount <= 0) throw new Error('Enter an amount.')
  const tempId = newTempId(device.terminal_unique_no)
  const now = new Date()
  // The slip's barcode (POS.md §18.8): a repayment has no order, so it carries its own ref.
  const receiptNo = await nextNumber(device.terminal_unique_no, now)
  await rows('credit_repayments').add({
    id: tempId,
    temp_id: tempId,
    synced: 0,
    receipt_no: receiptNo,
    terminal_id: device.terminal_id,
    ...(customer._id ? { customer_id: customer._id } : { customer_temp_id: customer.temp_id }),
    amount,
    payment_method: method,
    remark: remark || null,
    created_at: now.toISOString(),
  })
  syncEngine.requestSync()
  return { receipt_no: receiptNo, amount, customer: String(customer.name), created_at: now.toISOString() }
}
